import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { BehaviorSubject, catchError, combineLatest, map, NEVER, of, startWith, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EligibleUsersService } from './services/eligible-users.service';
import { EligibleUser } from './models/eligible-user';
import { CreatePersonContactRequest } from './models/create-person-contact-request';
import { ContactsService } from '../../services/contacts.service';
import { getInitials } from '../../../../../../components/avatar/avatar.utils';
import { AssignableParty } from '../add-department-contact/models/assignable-party';
import { ContactDetails } from '../contact-details/models/contact-details';

// Present only when opened from the Organisations Detail screen's "Create a new person
// contact…" fallback (see PeopleSectionComponent) — pre-assigns the new contact to that
// organisation instead of leaving it unassigned.
export interface AddPersonContactData {
  preselectedParty?: AssignableParty;
}

export type AddPersonContactResult = { action: 'Added'; contact: ContactDetails };

export const ELIGIBLE_USER_ROW_HEIGHT = 56;

// Minimum characters before the (client-side) search filters the list. Below this, the list is
// left exactly as it was — not cleared — so typing the first couple of characters doesn't make
// the results flash away and come back.
export const MIN_SEARCH_LENGTH = 3;

// This dialog intentionally bypasses the Material form-field/button theming (mat-form-field,
// mat-flat-button, etc.) in favor of plain elements + bespoke CSS in the .scss file, to match
// the design mockup exactly — Material's default appearance couldn't get there. Revisit once
// the contact page work is done and this look becomes a proper shared theme.
@Component({
  selector: 'app-add-person-contact',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIcon,
    MatProgressSpinner,
    ScrollingModule
  ],
  templateUrl: './add-person-contact.component.html',
  styleUrl: './add-person-contact.component.scss'
})
export class AddPersonContactComponent implements OnInit {
  protected readonly getInitials = getInitials;
  protected readonly rowHeight = ELIGIBLE_USER_ROW_HEIGHT;

  searchControl = new FormControl('', { nonNullable: true });
  loading = true;
  hasError = false;
  filteredUsers$ = of<EligibleUser[]>([]);
  selectedUser: EligibleUser | null = null;
  saving = false;

  private allUsers$ = new BehaviorSubject<EligibleUser[]>([]);
  private eligibleUsersService = inject(EligibleUsersService);
  private contactsService = inject(ContactsService);
  private matSnackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);
  private matDialogRef = inject(MatDialogRef<AddPersonContactComponent, AddPersonContactResult>);
  private data = inject<AddPersonContactData | null>(MAT_DIALOG_DATA, { optional: true });

  ngOnInit() {
    // Fetched once — filtering as the admin types happens entirely client-side afterwards
    // (mirrors the same fetch-once + local-filter pattern used by the main Contacts grid).
    this.eligibleUsersService
      .getEligibleUsers()
      .pipe(
        catchError(() => {
          this.hasError = true;
          return of<EligibleUser[]>([]);
        }),
        tap(users => {
          this.allUsers$.next(users);
          this.loading = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.filteredUsers$ = combineLatest([this.allUsers$, this.searchControl.valueChanges.pipe(startWith(''))]).pipe(
      map(([users, term]) => {
        const value = term.trim().toLowerCase();
        if (value.length < MIN_SEARCH_LENGTH) {
          return users;
        }
        return users.filter(
          user => user.name.toLowerCase().includes(value) || user.email.toLowerCase().includes(value)
        );
      })
    );
  }

  trackById(_: number, user: EligibleUser) {
    return user.id;
  }

  select(user: EligibleUser) {
    this.selectedUser = user;
  }

  save() {
    if (!this.selectedUser) {
      return;
    }

    // Person contacts stay in sync with the linked user account server-side (name, email,
    // phone, photo) — the API rejects (400) a request that also sends those fields, so only
    // `userId` may identify the contact here.
    const preselected = this.data?.preselectedParty;
    const request: CreatePersonContactRequest = {
      type: 'Person',
      userId: this.selectedUser.id,
      showMobilePhone: false,
      showPhoto: false,
      assignments: preselected ? [{ partyId: preselected.id, role: '', sortOrder: 0 }] : []
    };

    this.saving = true;
    this.contactsService
      .createPersonContact(request)
      .pipe(
        tap(contact => {
          this.matSnackBar.open('Person contact added', 'x', { panelClass: 'snackbar-green' });
          this.matDialogRef.close({ action: 'Added', contact });
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.saving = false;
          this.matSnackBar.open(this.errorMessageFor(error), 'x');
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private errorMessageFor(error: HttpErrorResponse): string {
    if (error.status === 404) {
      return 'This user could not be found. Please refresh the list and try again.';
    }
    if (error.status === 409 && error.error?.code === 'LinkedUserDuplicate') {
      return 'This user is already linked to a contact.';
    }
    return 'Could not add a new contact. Please try again.';
  }
}
